import AudioProcessorUrl from "~vue/RealtimeChat/audio-processor-v2.js?worker&url";
import { logError } from "~vue/utils/logUtils";
import { ref } from "vue";

const SAMPLE_RATE = 24000;
const CHANNEL_COUNT = 1;
// Minimum chunks to buffer before starting playback. Each chunk is
// ~170ms at 24kHz (4096 samples). 3 chunks ≈ 500ms of buffer, enough
// to absorb typical network jitter without noticeable startup delay.
const JITTER_BUFFER_MIN_CHUNKS = 3;

interface AudioChunk {
    delta: string;
}

interface ScheduledItem {
    source: AudioBufferSourceNode;
    chunk: AudioChunk;
    startTime: number;
}

interface UseRealtimeVoiceOptions {
    onMessageCallback?: (buffer: ArrayBuffer) => void;
}

const base64ToFloat32 = (base64: string): Float32Array<ArrayBuffer> => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }

    const int16 = new Int16Array(bytes.buffer.slice(0));
    return Float32Array.from(int16, (x) => x / 0x8000) as Float32Array<ArrayBuffer>;
};

export const useRealtimeVoice = ({ onMessageCallback }: UseRealtimeVoiceOptions) => {
    let context: AudioContext | undefined;
    let stream: MediaStream | undefined;
    let streamSource: MediaStreamAudioSourceNode | undefined;
    let analyser: AnalyserNode | undefined;
    let audioLevelInterval: ReturnType<typeof setInterval> | undefined;
    let scheduledEndTime = 0;
    let scheduledItems: ScheduledItem[] = [];
    let levelDataArray: Uint8Array<ArrayBuffer> | null = null;
    let chatEnded = false;

    const isPlaying = ref(false);
    const isMuted = ref(false);
    const isOutputMuted = ref(false);
    const audioLevel = ref(0);
    const audioQueue = ref<AudioChunk[]>([]);

    const startChat = async () => {
        chatEnded = false;
        isMuted.value = false;
        isOutputMuted.value = false;
        stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: SAMPLE_RATE,
                channelCount: CHANNEL_COUNT,
            },
        });

        context = new AudioContext({
            sampleRate: SAMPLE_RATE,
        });

        await context.audioWorklet.addModule(AudioProcessorUrl);
        const audioWorkletNode = new AudioWorkletNode(context, "audio-processor-v2");
        audioWorkletNode.port.onmessage = (event: MessageEvent) => {
            if (!isMuted.value) {
                onMessageCallback?.(event.data.buffer as ArrayBuffer);
            }
        };

        streamSource = context.createMediaStreamSource(stream);
        streamSource.connect(audioWorkletNode);

        analyser = context.createAnalyser();
        analyser.fftSize = 2048;
        levelDataArray = new Uint8Array(analyser.frequencyBinCount);

        streamSource.connect(analyser);

        audioLevelInterval = setInterval(() => {
            updateAudioLevel();
        }, 100);
    };

    const updateAudioLevel = () => {
        if (!analyser || !levelDataArray) {
            return;
        }

        if (isMuted.value) {
            audioLevel.value = 0;
            return;
        }

        analyser.getByteFrequencyData(levelDataArray);

        let sum = 0;
        for (let i = 0; i < levelDataArray.length; i++) {
            sum += levelDataArray[i];
        }
        const normalizedLevel = sum / (levelDataArray.length * 255);

        audioLevel.value = normalizedLevel;
    };

    const muteChat = () => {
        isMuted.value = true;
        // Only suppress mic — Nadia's audio keeps playing so the user
        // hears her finish. We do NOT stop playback here.
        stream?.getAudioTracks().forEach((track) => {
            track.enabled = false;
        });
    };

    const unmuteChat = () => {
        isMuted.value = false;
        // Re-enable mic tracks
        stream?.getAudioTracks().forEach((track) => {
            track.enabled = true;
        });
        // Kick the scheduler in case chunks arrived while muted
        scheduleNextChunks();
    };

    const scheduleNextChunks = ({ force = false }: { force?: boolean } = {}) => {
        if (isOutputMuted.value) {
            audioQueue.value = [];
            return;
        }

        if (audioQueue.value.length === 0) {
            return;
        }

        if (chatEnded) {
            isPlaying.value = false;
            audioQueue.value = [];
            return;
        }

        if (!context || context.state === "closed") {
            logError("Error playing audio: AudioContext is not initialized", {
                context: {
                    audioContext: {
                        exists: !!context,
                        state: context?.state ?? "null",
                    },
                    playback: {
                        queueLength: audioQueue.value.length,
                        scheduledItems: scheduledItems.length,
                        chatEnded,
                    },
                },
                tags: {
                    error_type: "audio_context_not_initialized",
                    context_state: context?.state ?? "null",
                },
            });
            isPlaying.value = false;
            return;
        }

        // Resume suspended AudioContext (common on mobile background/foreground
        // or desktop battery saver). Scheduling can proceed immediately —
        // queued sources will play once the context resumes.
        if (context.state === "suspended") {
            void context.resume();
        }

        // Jitter buffer: when nothing is scheduled yet (start of a new response),
        // wait until enough chunks have accumulated to absorb network timing
        // variation. Once playback is underway, schedule immediately.
        // `force` bypasses this — used when the response is done (listening state)
        // to flush any remaining tail chunks that would otherwise strand.
        if (!force && scheduledItems.length === 0 && audioQueue.value.length < JITTER_BUFFER_MIN_CHUNKS) {
            return;
        }

        try {
            while (audioQueue.value.length > 0) {
                const nextChunk = audioQueue.value.shift()!;
                const audioData = base64ToFloat32(nextChunk.delta);
                const buffer = new AudioBuffer({ numberOfChannels: 1, length: audioData.length, sampleRate: context.sampleRate });
                buffer.copyToChannel(audioData, 0);

                const source = new AudioBufferSourceNode(context, { buffer });
                source.connect(context.destination);

                const startTime = Math.max(scheduledEndTime, context.currentTime);
                source.start(startTime);
                scheduledEndTime = startTime + buffer.duration;

                scheduledItems.push({ source, chunk: nextChunk, startTime });
                source.onended = () => {
                    scheduledItems = scheduledItems.filter((item) => item.source !== source);
                    if (scheduledItems.length === 0 && audioQueue.value.length === 0) {
                        isPlaying.value = false;
                    }
                };
            }

            isPlaying.value = true;
        } catch (error) {
            logError(`Error playing audio: ${error}`, {
                context: {
                    audioContext: {
                        exists: !!context,
                        state: context?.state ?? "null",
                    },
                    playback: {
                        queueLength: audioQueue.value.length,
                        scheduledItems: scheduledItems.length,
                        chatEnded,
                    },
                },
                tags: {
                    error_type: "audio_playback_error",
                    context_state: context?.state ?? "null",
                },
            });
            isPlaying.value = false;
        }
    };

    const stopPlayback = ({ preserveUnplayed = true }: { preserveUnplayed?: boolean } = {}) => {
        const now = context?.currentTime ?? 0;
        // Re-queue chunks that haven't started playing yet so playback can
        // resume if the interruption was a false VAD trigger.
        const unplayed = preserveUnplayed ? scheduledItems.filter((item) => item.startTime > now).map((item) => item.chunk) : [];
        for (const { source } of scheduledItems) {
            try {
                source.stop();
                source.disconnect();
            } catch {
                // Source may have already ended
            }
        }
        scheduledItems = [];
        scheduledEndTime = 0;
        isPlaying.value = false;
        audioQueue.value = [...unplayed, ...audioQueue.value];
    };

    const muteOutputAudio = () => {
        isOutputMuted.value = true;
        stopPlayback({ preserveUnplayed: false });
        audioQueue.value = [];
    };

    const unmuteOutputAudio = () => {
        isOutputMuted.value = false;
        scheduleNextChunks();
    };

    const endChat = async () => {
        chatEnded = true;
        isMuted.value = false;
        isOutputMuted.value = false;
        stopPlayback();
        if (audioLevelInterval) {
            clearInterval(audioLevelInterval);
        }
        stream?.getTracks().forEach((track) => track.stop());
        streamSource?.disconnect();
        streamSource = undefined;
        analyser?.disconnect();
        analyser = undefined;
        levelDataArray = null;
        await context?.close();
        context = undefined;
        audioLevel.value = 0;
    };

    return {
        startChat,
        endChat,
        muteChat,
        unmuteChat,
        audioLevel,
        isPlaying,
        isMuted,
        isOutputMuted,
        audioQueue,
        scheduleNextChunks,
        stopPlayback,
        muteOutputAudio,
        unmuteOutputAudio,
    };
};
